import React, { useState, useEffect } from 'react';
import '../css/PurchaseModal.css';

export default function PurchaseModal({
  isOpen,
  onClose,
  onConfirm,
  type = 'credits', // 'credits', 'talentRole', or 'shortlistPass'
  currentBalance = 0,
  price = 0
}) {
  const [quantity, setQuantity] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setShowConfirm(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isCredits = type === 'credits';
  const isTalentRole = type === 'talentRole';
  const isShortlistPass = type === 'shortlistPass';
  const itemName = isCredits ? 'Screening Credits' : isTalentRole ? 'Talent Role Passes' : 'Shortlist Passes';
  const itemIcon = isCredits ? '🎫' : isTalentRole ? '👥' : '📋';
  const totalPrice = price * quantity;
  const newBalance = Number(currentBalance) + Number(quantity);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setQuantity(Math.max(1, Math.min(99, value)));
  };

  const handleConfirm = () => {
    onConfirm?.(quantity, totalPrice);
    onClose?.();
  };

  const handleBack = () => {
    setShowConfirm(false);
  };

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Floating particles for gamified effect */}
        <div className="pm-particles">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} style={{ '--i': i }} />
          ))}
        </div>

        {!showConfirm ? (
          // Quantity Selection Screen
          <>
            <div className="pm-header">
              <div className="pm-icon">{itemIcon}</div>
              <h3 className="pm-title">Purchase {itemName}</h3>
              <p className="pm-subtitle">How many would you like to add?</p>
            </div>

            <div className="pm-quantity-section">
              <div className="pm-current-balance">
                <span className="pm-balance-label">Current Balance:</span>
                <span className="pm-balance-value">{currentBalance}</span>
              </div>

              <div className="pm-quantity-input">
                <label htmlFor="quantity">Quantity:</label>
                <div className="pm-quantity-controls">
                  <button 
                    className="pm-qty-btn pm-qty-minus"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    −
                  </button>
                  <input
                    id="quantity"
                    type="number"
                    min="1"
                    max="99"
                    value={quantity}
                    onChange={handleQuantityChange}
                    className="pm-qty-input"
                  />
                  <button 
                    className="pm-qty-btn pm-qty-plus"
                    onClick={() => setQuantity(Math.min(99, quantity + 1))}
                    disabled={quantity >= 99}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="pm-price-breakdown">
                <div className="pm-price-item">
                  <span>{quantity} × {itemName}</span>
                  <span>${price.toFixed(2)} each</span>
                </div>
                <div className="pm-price-total">
                  <span>Total:</span>
                  <span className="pm-total-price">${totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <div className="pm-new-balance">
                <span className="pm-balance-label">New Balance:</span>
                <span className="pm-balance-value pm-balance-new">{newBalance}</span>
              </div>
            </div>

            <div className="pm-actions">
              <button className="pm-btn pm-btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button 
                className="pm-btn pm-btn-primary"
                onClick={() => setShowConfirm(true)}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          // Confirmation Screen
          <>
            <div className="pm-header">
              <div className="pm-icon pm-icon-confirm">💳</div>
              <h3 className="pm-title">Confirm Purchase</h3>
              <p className="pm-subtitle">Are you sure you want to proceed?</p>
            </div>

            <div className="pm-confirm-section">
              <div className="pm-purchase-summary">
                <div className="pm-summary-item">
                  <span className="pm-summary-label">Item:</span>
                  <span className="pm-summary-value">{quantity} × {itemName}</span>
                </div>
                <div className="pm-summary-item">
                  <span className="pm-summary-label">Price per item:</span>
                  <span className="pm-summary-value">${price.toFixed(2)}</span>
                </div>
                <div className="pm-summary-item pm-summary-total">
                  <span className="pm-summary-label">Total Cost:</span>
                  <span className="pm-summary-value">${totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <div className="pm-balance-change">
                <div className="pm-balance-before">
                  <span>Current Balance:</span>
                  <span>{currentBalance}</span>
                </div>
                <div className="pm-balance-arrow">→</div>
                <div className="pm-balance-after">
                  <span>New Balance:</span>
                  <span className="pm-balance-new">{newBalance}</span>
                </div>
              </div>
            </div>

            <div className="pm-actions">
              <button className="pm-btn pm-btn-ghost" onClick={handleBack}>
                Back
              </button>
              <button 
                className="pm-btn pm-btn-success"
                onClick={handleConfirm}
              >
                Confirm Purchase
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
